import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';

// Hero component to display a customizable hero section
const Hero = ({
  title = "Learn. Innovate. Connect. Have Fun.", // Default title for the hero section
  description = "Join the ACM at ACC for more than just coding and tech workshops. From hackathons to game nights, we're a community where learning and laughter go hand in hand.", // Default description
  primaryButtonText = "Get Started", // Default primary button text
  primaryButtonLink = "/join", // Default primary button link
  secondaryButtonText = "Learn More", // Default secondary button text
  secondaryButtonLink = "/about", // Default secondary button link
  imageSrc = "", // Default image source (empty by default)
}) => {
  return (
    <Box 
      sx={{ 
        position: 'relative',
        isolation: 'isolate',
        pt: { xs: 14, sm: 16, md: 20 },
        pb: { xs: 8, sm: 12, md: 16 },
        px: { xs: 2, sm: 4, md: 6 },
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Main content container */}
      <Container maxWidth="lg">
        <Stack 
          direction={{ xs: 'column', md: 'row' }} // Column on small screens, row on larger screens
          spacing={{ xs: 8, md: 10 }} // Spacing between elements
          alignItems="center" // Aligns items to the center
          justifyContent="space-between" // Space between title/description and image
        >
          {/* Text Content Section */}
          <Stack 
            spacing={4} // Space between elements in the stack
            alignItems={{ xs: 'center', md: 'flex-start' }} // Center-aligned on small screens, left-aligned on larger screens
            textAlign={{ xs: 'center', md: 'left' }} // Center text on small screens, left-aligned on larger screens
            sx={{ 
              flex: '0 1 50%', // Allocates 50% width of the container
              maxWidth: { md: '50%' }, // Limits max width for medium screens and above
              zIndex: 1, // Ensures it appears above the background gradient
            }}
          >
            {/* Hero Title */}
            <Typography
              variant="h1" // Heading 1 style
              sx={{
                fontSize: { xs: '2rem', sm: '3rem', md: '4rem' }, // Responsive font sizes
                fontWeight: 600, // Bold font weight
                letterSpacing: '-0.02em', // Slightly condensed letter spacing
                lineHeight: 1.2, // Line height for better readability
                color: 'text.primary', // Primary text color from theme
              }}
            >
              {title} {/* Dynamically rendered title */}
            </Typography>

            {/* Hero Description */}
            <Typography
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem' }, // Responsive font sizes
                lineHeight: 1.8, // Line height for better readability
                fontWeight: 500, // Medium font weight
                color: 'text.secondary', // Secondary text color from theme
                maxWidth: '42rem', // Limits the max width for text
              }}
            >
              {description} {/* Dynamically rendered description */}
            </Typography>

            {/* Action Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={3}
              justifyContent={{ xs: 'center', md: 'flex-start' }}
              alignItems="center"
              mt={6}
            >
              {/* Removing the primary button section entirely */}
            </Stack>
          </Stack>

          {/* Hero Image Section */}
          {imageSrc && (
            <Box
              sx={{
                position: 'relative',
                flex: '0 1 50%', // Allocates 50% width of the container
                display: { xs: 'none', md: 'block' }, // Hides on small screens
                maxWidth: { md: '50%' }, // Limits max width for medium screens and above
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '500px', // Limits image container width
                  aspectRatio: '1', // Maintains square aspect ratio
                  margin: '0 auto', // Centers the image container
                  transform: 'rotate(-45deg)', // Rotates the image for a dynamic effect
                  transition: 'transform 0.3s ease-in-out', // Smooth animation on hover
                  '&:hover': {
                      transform: 'rotate(-45deg) scale(1.05)', // Enlarges image on hover
                  },
                }}
              >
                {/* Background Shadow Box */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '10%',
                    left: '10%',
                    width: '100%',
                    height: '100%',
                    borderRadius: '2rem', // Rounded corners
                    bgcolor: 'primary.main', // Primary color from theme
                    opacity: 0.1, // Light shadow effect
                  }}
                />
                {/* Main Image */}
                <Box
                  component="img"
                  src={imageSrc} // Dynamically rendered image source
                  alt="Hero Section Image" // Alt text for accessibility
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover', // Ensures the image covers the container
                    borderRadius: '2rem', // Rounded corners
                    boxShadow: theme => `0 24px 48px ${theme.palette.purple.main}40`, // Custom shadow
                  }}
                />
              </Box>
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default Hero;